'use client';

import {
  Box,
  Divider,
  Pagination,
  SystemIcon,
  Typography,
} from '@wishket/design-system';
import { useMemo, useState } from 'react';

import CollectionArticleItemWidget from '../CollectionArticleItemWidget';

import { Tooltip } from './CollectionContentsList.parts';

import { Collection } from '@/shared/types';
import { useDeviceCheck } from '@/shared/hooks';
import { CollectionEmpty } from '@/features/Collection';
import { useDeleteScrappedArticleMutation } from '@/entities/collection';

interface CollectionContentsListProps {
  isOwner?: boolean;
  collectionId: number;
  hideAddButton?: boolean;
  articleList: Collection['scraps'];
  onAddExternalContent?: (open: boolean) => void;
}

const CollectionContentsList = ({
  isOwner,
  articleList,
  collectionId,
  hideAddButton = false,
  onAddExternalContent,
}: CollectionContentsListProps) => {
  const { isDesktop } = useDeviceCheck();
  const [currentPage, setCurrentPage] = useState(1);

  const { mutate: deleteArticleFromCollection } =
    useDeleteScrappedArticleMutation();

  const hasExternalArticle = useMemo(() => {
    return articleList.some((item) => item.externalContent.url);
  }, [articleList]);

  const totalPage = Math.ceil(articleList.length / 10);
  const currentList = articleList.slice(
    (currentPage - 1) * 10,
    currentPage * 10
  );

  const handlePagination = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({
      top: 0,
    });
  };

  const handleDelete = (id: number) => {
    deleteArticleFromCollection({ collectionId: collectionId, scrapId: id });
  };

  const openExternalContentModal = () => {
    if (onAddExternalContent) {
      onAddExternalContent(true);
    }
  };

  return (
    <Box className="flex flex-col">
      <Box className="flex items-center justify-between">
        <Box className="flex gap-2">
          <Typography
            variant="subTitle1"
            className="font-medium text-w-gray-900"
          >
            콘텐츠
          </Typography>
          <Typography
            variant="subTitle1"
            className="font-medium text-primary-500"
          >
            {articleList.length}
          </Typography>
        </Box>
        {!hideAddButton && (
          <Box className="group relative">
            <Box
              onClick={openExternalContentModal}
              className="flex h-9 cursor-pointer items-center gap-1 rounded-[18px] border border-w-gray-200 px-[11px] desktop:hover:border-primary-500"
            >
              <SystemIcon name="small_plus" className="text-w-gray-600" />
              <Typography variant="body2" className="text-w-gray-600">
                URL 추가
              </Typography>
            </Box>
            {!hasExternalArticle && (
              <Box className="absolute bottom-full right-0 mb-[13px]">
                <Tooltip>외부 뉴스, 웹페이지도 컬렉션에 모아보세요.</Tooltip>
              </Box>
            )}
          </Box>
        )}
      </Box>
      {currentList.length > 0 ? (
        <Box className="mt-8 flex flex-col gap-8">
          {currentList.map((item, index) => (
            <Box key={item.id} className="flex flex-col gap-8">
              <CollectionArticleItemWidget
                isVisibleCategory
                isOwner={isOwner}
                dateCreated={item.dateCreated}
                onClickDelete={() => handleDelete(item.id)}
                {...item.news}
              />
              {index !== currentList.length - 1 && <Divider />}
            </Box>
          ))}
          <Box className="mt-12">
            <Pagination
              page={currentPage}
              maxPage={totalPage}
              pageCount={isDesktop ? 10 : 5}
              onChange={handlePagination}
            />
          </Box>
        </Box>
      ) : (
        <Box className="mt-20">
          <CollectionEmpty type="collection" />
        </Box>
      )}
    </Box>
  );
};

export default CollectionContentsList;
